import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/lib/lumiqoData";

const Avatar = ({ name, photo }) => (
  <img
    src={photo}
    alt={name}
    loading="lazy"
    className="w-12 h-12 rounded-full object-cover bg-[#f5f6fa] ring-2 ring-white"
    data-testid={`testimonial-avatar-${name.replace(/\s+/g, "-").toLowerCase()}`}
  />
);

const cardVariants = {
  enter: (dir) => ({ opacity: 0, x: 48 * dir }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
  exit: (dir) => ({
    opacity: 0,
    x: -48 * dir,
    transition: { duration: 0.3, ease: [0.4, 0, 1, 1] },
  }),
};

const Card = ({ t, i, direction }) => (
  <motion.div
    custom={direction}
    variants={cardVariants}
    initial="enter"
    animate="center"
    exit="exit"
    className="relative p-7 rounded-3xl bg-white border border-[#0a1d4d]/10 lq-shadow-soft min-h-[260px] flex flex-col"
    data-testid={`testimonial-card-${i}`}
  >
    <Quote className="w-7 h-7 text-[#1d72ff]/30" />
    <p className="mt-4 text-[#0a1d4d] text-base leading-relaxed font-medium">
      “{t.quote}”
    </p>
    <div className="mt-auto pt-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Avatar name={t.name} photo={t.photo} />
        <div>
          <div className="font-semibold text-[#0a1d4d] text-sm">{t.name}</div>
          <div className="text-xs text-[#0a1d4d]/60">{t.role}</div>
        </div>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: t.rating }).map((_, k) => (
          <Star
            key={k}
            className="w-3.5 h-3.5 text-[#1d72ff] fill-[#1d72ff]"
          />
        ))}
      </div>
    </div>
  </motion.div>
);

const AUTOPLAY_MS = 5000;
const RESUME_AFTER_MS = 2000;

const Testimonials = () => {
  const [start, setStart] = useState(0);
  const [direction, setDirection] = useState(1);
  const visible = 3;
  const total = TESTIMONIALS.length;
  const autoplayRef = useRef(null);
  const resumeRef = useRef(null);

  const goNext = useCallback(() => {
    setDirection(1);
    setStart((s) => (s + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setStart((s) => (s - 1 + total) % total);
  }, [total]);

  const startAutoplay = useCallback(() => {
    clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(goNext, AUTOPLAY_MS);
  }, [goNext]);

  useEffect(() => {
    startAutoplay();
    return () => {
      clearInterval(autoplayRef.current);
      clearTimeout(resumeRef.current);
    };
  }, [startAutoplay]);

  // Manual navigation: pause autoplay, then smoothly resume after 2s.
  const manual = (fn) => () => {
    clearInterval(autoplayRef.current);
    clearTimeout(resumeRef.current);
    fn();
    resumeRef.current = setTimeout(startAutoplay, RESUME_AFTER_MS);
  };

  const items = Array.from({ length: visible }).map(
    (_, i) => TESTIMONIALS[(start + i) % total]
  );

  return (
    <section
      id="testimonials"
      className="relative py-24 sm:py-32 bg-[#f5f6fa]"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full bg-[#1d72ff]/10 text-[#1d72ff] text-xs font-semibold tracking-wide uppercase">
              Testimonials
            </div>
            <h2
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0a1d4d] mt-5 leading-[1.05]"
              data-testid="testimonials-heading"
            >
              Loved by creators
              <br />
              who care about growth.
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={manual(goPrev)}
              className="w-11 h-11 rounded-full bg-white border border-[#0a1d4d]/10 flex items-center justify-center text-[#0a1d4d] hover:bg-[#0a1d4d] hover:text-white transition-colors"
              data-testid="testimonials-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={manual(goNext)}
              className="w-11 h-11 rounded-full bg-[#0a1d4d] text-white flex items-center justify-center hover:bg-[#1d72ff] transition-colors"
              data-testid="testimonials-next"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          <AnimatePresence mode="popLayout" custom={direction}>
            {items.map((t, i) => (
              <Card key={`${t.name}-${start}-${i}`} t={t} i={i} direction={direction} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
